import { z } from "zod";

/**
 * Audience Q&A.
 *
 * A question is visible to the room only once it is `approved` (or `answered`). With
 * moderation off it is approved on arrival; with moderation on it waits for the
 * presenter. `rejected` questions are never shown to participants, and a question
 * rejected after being shown disappears from their screens.
 */
export const QNA_STATUSES = ["pending", "approved", "answered", "rejected"] as const;
export const qnaStatusSchema = z.enum(QNA_STATUSES);
export type QnaStatus = z.infer<typeof qnaStatusSchema>;

/** Statuses the room can see. */
export const VISIBLE_QNA_STATUSES = ["approved", "answered"] as const satisfies readonly QnaStatus[];

export function isVisibleQnaStatus(status: QnaStatus): boolean {
  return (VISIBLE_QNA_STATUSES as readonly string[]).includes(status);
}

/**
 * One question. The same shape for presenter and participants: the author is a display
 * name or null (anonymous), never a participant id, so nothing here links a question
 * back to the person who asked it.
 */
export const qnaQuestionSchema = z
  .object({
    questionId: z.string().uuid(),
    body: z.string(),
    authorName: z.string().nullable(),
    status: qnaStatusSchema,
    upvoteCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type QnaQuestion = z.infer<typeof qnaQuestionSchema>;

/** Presenter → server: moderate one question. Back to `pending` is not a thing. */
export const qnaModerateSchema = z
  .object({ status: z.enum(["approved", "answered", "rejected"]) })
  .strict();

export type QnaModerateRequest = z.infer<typeof qnaModerateSchema>;

/** Reply to the participant's `qna.list` RPC. */
export const qnaListResultSchema = z
  .object({
    enabled: z.boolean(),
    questions: z.array(qnaQuestionSchema),
    /** Question ids this participant has upvoted, so their buttons render pressed. */
    myVotes: z.array(z.string().uuid()),
  })
  .strict();

export type QnaListResult = z.infer<typeof qnaListResultSchema>;
